import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "./firebaseConfig";
import { useNavigate } from "react-router-dom";
import {
  BarChart3, CalendarDays, CheckCircle2, ChevronDown, ClipboardCheck,
  ClipboardCopy, ClipboardList, Download, Edit3, FileQuestion, FileText, Lock,
  MoreHorizontal, Plus, Search, Sparkles, Trash2, Unlock, UsersRound,
  LibraryBig,
} from "lucide-react";
import { exportQuizToWord, fetchQuizQuestions } from "./utils/wordExportService";
import { deleteQuizApi } from "./utils/quizDeletionApi";
import AssignAssessmentModal from "./AssignAssessmentModal";
import OrganisationPublishDialog from "./OrganisationPublishDialog";
import { accountDisplayName, DashboardHeader, DashboardSidebar } from "./Dashboard";
import "./ViewQuizzes.css";

const accentIndexFor = (value) =>
  Array.from(String(value || "quiz")).reduce((total, character) => total + character.charCodeAt(0), 0) % 5;

const createdAtMillis = (createdAt) => {
  const seconds = createdAt?.seconds;
  const nanoseconds = createdAt?.nanoseconds ?? 0;
  if (!Number.isInteger(seconds) || seconds <= 0 ||
      !Number.isInteger(nanoseconds) || nanoseconds < 0 || nanoseconds >= 1e9) return null;
  const millis = seconds * 1000 + nanoseconds / 1e6;
  return Number.isFinite(new Date(millis).getTime()) ? millis : null;
};

export const selectRecentTeacherQuizzes = (quizzes, teacherUid) =>
  quizzes.filter((quiz) => quiz.creatorUid === teacherUid && createdAtMillis(quiz.createdAt) !== null)
    .sort((left, right) => createdAtMillis(right.createdAt) - createdAtMillis(left.createdAt) ||
      String(left.id).localeCompare(String(right.id)))
    .slice(0, 5);

// ─── Individual Quiz Card ────────────────────────────────────────
const QuizCard = ({
  quiz,
  user,
  onLock,
  onUnlock,
  onDelete,
  onViewResults,
  onAssign,
  onDuplicate,
  onExportWord,
  isExporting,
  onEdit,
  onShare,
  isDropdownOpen,
  onToggleDropdown,
  isDeleting,
  accentIndex,
}) => {
  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onToggleDropdown(null);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen, onToggleDropdown]);

  const creationMillis = createdAtMillis(quiz.createdAt);
  const formattedDate = creationMillis !== null
    ? new Date(creationMillis).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "N/A";

  const statusClass = quiz.locked ? "locked" : "active";
  const displayTitle = quiz.quizTitle || quiz.title || quiz.quizCode;

  return (
    <article className={`vq-card vq-tone-${accentIndex % 5}${isDropdownOpen ? " dropdown-open" : ""}`}>
      <span className="vq-card-corner" aria-hidden="true" />
      <div className="vq-card-top">
        <div className="vq-card-icon" aria-hidden="true"><FileText size={22} /></div>
        <div className="vq-card-title-row">
          <div>
            <p className="vq-card-subject">{quiz.subject || "Uncategorized"}</p>
            <h3 className="vq-card-title">
              <button type="button" onClick={() => onViewResults(quiz.id)} title="View Results">{displayTitle}</button>
            </h3>
          </div>
          <div className="vq-card-badges">
            {quiz.hasAssignments === true && <span className="vq-badge assigned"><UsersRound size={12} />Assigned</span>}
            <span className={`vq-badge ${statusClass}`}>
              {quiz.locked ? <Lock size={12} /> : <CheckCircle2 size={12} />}{quiz.locked ? "Locked" : "Active"}
            </span>
          </div>
        </div>

        <div className="vq-card-meta">
          <span className="vq-card-meta-item">
            <ClipboardCopy size={14} />
            <span><small>Test code</small><strong>{quiz.quizCode}</strong></span>
          </span>
          <span className="vq-card-meta-item">
            <CalendarDays size={14} />
            <span><small>Created</small><strong>{formattedDate}</strong></span>
          </span>
          <span className="vq-card-meta-item">
            <FileQuestion size={14} />
            <span><small>Questions</small><strong>{quiz.questionIds?.length || 0}</strong></span>
          </span>
        </div>
      </div>

      {(user?.role === "admin" || user?.role === "teacher") && (
        <div className="vq-card-actions">
          {user?.role === "teacher" && (
            <button className="vq-action-btn assign" onClick={(event) => onAssign(quiz, event.currentTarget)}>
              <ClipboardCheck size={15} />Assign
            </button>
          )}
          <button className="vq-action-btn primary" onClick={() => onViewResults(quiz.id)}>
            <BarChart3 size={15} />Results
          </button>
          <button className="vq-action-btn edit" onClick={() => onEdit(quiz.id)}>
            <Edit3 size={15} />Edit
          </button>
          {quiz.locked ? (
            <button className="vq-action-btn unlock" onClick={() => onUnlock(quiz.id)}>
              <Unlock size={14} />Unlock
            </button>
          ) : (
            <button className="vq-action-btn lock" onClick={() => onLock(quiz.id)}>
              <Lock size={14} />Lock
            </button>
          )}
          <div className="vq-more-wrapper" ref={menuRef}>
            <button type="button" className="vq-more-btn" onClick={() => onToggleDropdown(isDropdownOpen ? null : quiz.id)}
              title="More actions" aria-label={`More actions for ${displayTitle}`} aria-expanded={isDropdownOpen}>
              <MoreHorizontal size={18} />
            </button>

            {isDropdownOpen && (
              <div className="vq-dropdown">
                {user?.role === "teacher" && quiz.creatorUid === user.uid && <button className="vq-dropdown-item" onClick={() => { onShare(quiz); onToggleDropdown(null); }}>
                  <LibraryBig size={15} />Share Test to Organisation
                </button>}
                <button className="vq-dropdown-item" onClick={() => { onDuplicate(quiz.id); onToggleDropdown(null); }}>
                  <ClipboardList size={15} />Duplicate Test
                </button>
                <button className="vq-dropdown-item" onClick={() => { onExportWord(quiz); onToggleDropdown(null); }} disabled={isExporting}>
                  <Download size={15} />{isExporting ? "Exporting…" : "Export Word Docs"}
                </button>
                <div className="vq-dropdown-divider" />
                <button className="vq-dropdown-item danger" disabled={isDeleting}
                  onClick={() => { if (isDeleting) return; onDelete(quiz.id); onToggleDropdown(null); }}>
                  <Trash2 size={15} />{isDeleting ? "Deleting…" : "Delete Test"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
};

// ─── Main ViewQuizzes Component ──────────────────────────────────
const ViewQuizzes = ({ user }) => {
  const [quizzes, setQuizzes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [exportingQuizId, setExportingQuizId] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null); // quiz.id of card with open menu
  const [assigningQuiz, setAssigningQuiz] = useState(null);
  const [sharingQuiz, setSharingQuiz] = useState(null);
  const [deletingQuizIds, setDeletingQuizIds] = useState(() => new Set());
  const deletingQuizIdsRef = useRef(new Set());
  const assignTriggerRef = useRef(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copiedTeacherCode, setCopiedTeacherCode] = useState(false);
  const navigate = useNavigate();

  // Redirect non-admin/teacher users
  useEffect(() => {
    if (user && user.role !== "admin" && user.role !== "teacher") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    document.documentElement.setAttribute("data-dark-mode", darkMode ? "true" : "false");
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "darkMode") setDarkMode(event.newValue === "true");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Fetch quizzes from Firestore with role-based filtering
  useEffect(() => {
    const fetchQuizzes = async () => {
      if (!user) return;
      setIsLoading(true);
      try {
        let quizzesQuery;
        if (user.role === "admin") {
          quizzesQuery = collection(db, "quizzes");
        } else if (user.role === "teacher") {
          quizzesQuery = query(
            collection(db, "quizzes"),
            where("creatorUid", "==", user.uid)
          );
        } else {
          return;
        }

        const querySnapshot = await getDocs(quizzesQuery);
        const fetchedQuizzes = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setQuizzes(fetchedQuizzes);

        // Initialize all subjects as collapsed
        const subjects = [
          ...new Set(fetchedQuizzes.map((q) => q.subject || "Uncategorized")),
        ];
        const initial = {};
        subjects.forEach((s) => (initial[s] = false));
        setExpandedSubjects(initial);
      } catch (error) {
        console.error("Error fetching quizzes:", error.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchQuizzes();
  }, [user]);

  // Group quizzes by subject with search filtering
  const quizzesBySubject = useMemo(() => {
    if (!quizzes.length) return {};
    const term = (searchTerm || "").trim().toLowerCase();

    const filtered = term
      ? quizzes.filter((quiz) => {
          const code = (quiz.quizCode || "").toLowerCase();
          const title = (quiz.quizTitle || "").toLowerCase();
          const altTitle = (quiz.title || "").toLowerCase();
          const subject = (quiz.subject || "").toLowerCase();
          return (
            code.includes(term) ||
            title.includes(term) ||
            altTitle.includes(term) ||
            subject.includes(term)
          );
        })
      : quizzes;

    return filtered.reduce((acc, quiz) => {
      const subject = quiz.subject || "Uncategorized";
      if (!acc[subject]) acc[subject] = [];
      acc[subject].push(quiz);
      return acc;
    }, {});
  }, [quizzes, searchTerm]);

  // Auto-expand subjects when searching
  useEffect(() => {
    if (!(searchTerm || "").trim()) return;
    setExpandedSubjects((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(quizzesBySubject).forEach((subject) => {
        if (!next[subject]) {
          next[subject] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [searchTerm, quizzesBySubject]);

  const toggleSubject = useCallback((subject) => {
    setExpandedSubjects((prev) => ({ ...prev, [subject]: !prev[subject] }));
  }, []);

  // ─── Handlers (unchanged logic) ───

  const handleDeleteQuiz = useCallback(
    async (quizId) => {
      if (deletingQuizIdsRef.current.has(quizId)) return;
      if (
        !window.confirm(
          "Are you sure you want to delete this test? This action cannot be undone."
        )
      )
        return;
      deletingQuizIdsRef.current.add(quizId);
      setDeletingQuizIds((current) => new Set(current).add(quizId));
      try {
        const result = await deleteQuizApi(quizId);
        setQuizzes((prev) => prev.filter((q) => q.id !== quizId));
        alert(
          result.cleanupPending
            ? "Test deleted successfully. Some learning-content media cleanup is still pending and can be retried safely."
            : "Test deleted successfully!"
        );
      } catch (error) {
        console.error("Error deleting quiz:", error.message);
        const message = error.message?.replace(/^Assigned quizzes cannot be deleted\./, "Assigned tests cannot be deleted.");
        alert(`Failed to delete test: ${message || "Please try again."}`);
      } finally {
        deletingQuizIdsRef.current.delete(quizId);
        setDeletingQuizIds((current) => {
          const next = new Set(current);
          next.delete(quizId);
          return next;
        });
      }
    },
    []
  );

  const handleLockQuiz = useCallback(async (quizId) => {
    try {
      await updateDoc(doc(db, "quizzes", quizId), { locked: true });
      setQuizzes((prev) =>
        prev.map((q) => (q.id === quizId ? { ...q, locked: true } : q))
      );
      alert("Test locked successfully!");
    } catch (error) {
      console.error("Error locking quiz:", error.message);
      alert("Failed to lock test. Please try again.");
    }
  }, []);

  const handleUnlockQuiz = useCallback(async (quizId) => {
    try {
      await updateDoc(doc(db, "quizzes", quizId), { locked: false });
      setQuizzes((prev) =>
        prev.map((q) => (q.id === quizId ? { ...q, locked: false } : q))
      );
      alert("Test unlocked successfully!");
    } catch (error) {
      console.error("Error unlocking quiz:", error.message);
      alert("Failed to unlock test. Please try again.");
    }
  }, []);

  const handleDuplicateQuiz = useCallback(
    (quizId) => {
      navigate(`/create-quiz?duplicate=${quizId}`);
    },
    [navigate]
  );

  const handleEditQuiz = useCallback(
    (quizId) => {
      navigate(`/edit-quiz/${quizId}`);
    },
    [navigate]
  );

  const handleExportWord = useCallback(
    async (quiz) => {
      if (!quiz || !quiz.questionIds || quiz.questionIds.length === 0) {
        alert("This test has no questions to export.");
        return;
      }
      setExportingQuizId(quiz.id);
      try {
        const questions = await fetchQuizQuestions(db, quiz.questionIds);
        if (questions.length === 0) {
          alert("Could not fetch questions for this test.");
          setExportingQuizId(null);
          return;
        }
        const result = await exportQuizToWord(
          {
            quizCode: quiz.quizCode,
            subject: quiz.subject,
            topic: quiz.topic,
            level: quiz.level,
            specCode: quiz.specCode,
          },
          questions
        );
        if (result.success) {
          alert(
            "Word documents generated successfully! Check your downloads folder."
          );
        } else {
          alert(`Failed to generate Word documents: ${result.error}`);
        }
      } catch (error) {
        console.error("Error exporting quiz to Word:", error);
        alert("Failed to export test. Please try again.");
      } finally {
        setExportingQuizId(null);
      }
    },
    []
  );

  const handleViewResults = useCallback(
    (quizId) => {
      navigate(`/quiz-results/${quizId}`);
    },
    [navigate]
  );

  const handleOpenAssign = useCallback((quiz, triggerElement) => {
    assignTriggerRef.current = triggerElement;
    setOpenDropdown(null);
    setAssigningQuiz(quiz);
  }, []);

  const handleCloseAssign = useCallback(() => {
    setAssigningQuiz(null);
  }, []);

  const handleAssignmentResult = useCallback((quizId, result) => {
    const hasConfirmedAssignment =
      result.created.length > 0 || result.alreadyAssigned.length > 0;
    if (!hasConfirmedAssignment) return;

    setQuizzes((previous) =>
      previous.map((quiz) =>
        quiz.id === quizId ? { ...quiz, hasAssignments: true } : quiz
      )
    );
  }, []);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  const handleCopyTeacherCode = async () => {
    if (!user?.teacherCode) return;
    try {
      await navigator.clipboard.writeText(user.teacherCode);
      setCopiedTeacherCode(true);
      window.setTimeout(() => setCopiedTeacherCode(false), 1800);
    } catch (error) {
      console.error("Could not copy teacher code", error);
    }
  };

  const role = user?.role === "admin" ? "admin" : "teacher";
  const username = accountDisplayName(user, "StudyDen teacher");
  const assignedQuizCount = quizzes.filter((quiz) => quiz.hasAssignments === true).length;
  const totalQuestionCount = quizzes.reduce(
    (total, quiz) => total + (Array.isArray(quiz.questionIds) ? quiz.questionIds.length : 0),
    0
  );
  const recentQuizzes = useMemo(() =>
    user?.role === "teacher" ? selectRecentTeacherQuizzes(quizzes, user.uid) : [],
    [quizzes, user?.role, user?.uid]
  );

  const renderQuizCard = (quiz, recent = false) => (
    <QuizCard key={quiz.id} quiz={quiz} user={user} onLock={handleLockQuiz}
      onUnlock={handleUnlockQuiz} onDelete={handleDeleteQuiz} onDuplicate={handleDuplicateQuiz}
      onEdit={handleEditQuiz} onAssign={handleOpenAssign} onViewResults={handleViewResults}
      onShare={setSharingQuiz}
      onExportWord={handleExportWord} isExporting={exportingQuizId === quiz.id}
      isDropdownOpen={openDropdown === (recent ? `recent:${quiz.id}` : quiz.id)}
      onToggleDropdown={(id) => setOpenDropdown(id && recent ? `recent:${id}` : id)}
      isDeleting={deletingQuizIds.has(quiz.id)} accentIndex={accentIndexFor(quiz.id || quiz.quizCode)} />
  );

  // ─── Render ───

  return (
    <div className={`sd-dashboard sd-role-${role} sd-brand-theme vq-page${darkMode ? " sd-dark" : ""}`}>
      <DashboardSidebar
        role={role}
        username={username}
        creditsRemaining={user?.creditsRemaining ?? null}
        teacherCode={user?.teacherCode || ""}
        onCopyCode={handleCopyTeacherCode}
        copied={copiedTeacherCode}
        onLogout={handleLogout}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePath="/view-quizzes"
        officialDashboardBrand
      />
      <div className="sd-workspace vq-workspace">
        <DashboardHeader
          username={username}
          role={role}
          darkMode={darkMode}
          onToggleTheme={() => setDarkMode((value) => !value)}
          onOpenMenu={() => setSidebarOpen(true)}
          search={searchTerm}
          onSearch={setSearchTerm}
        />
        <main className="sd-main vq-main">
          <section className="vq-hero">
            <div className="vq-hero-copy">
              <p className="vq-eyebrow">Test Management</p>
              <h1>My Tests</h1>
              <p>Create, manage, assign, and review your assessments from one focused workspace.</p>
              <button type="button" className="vq-create-primary" onClick={() => navigate("/create-quiz")}>
                <Plus size={17} />Create Test
              </button>
            </div>
            <div className="vq-hero-art" aria-hidden="true">
              <span className="vq-hero-icon"><ClipboardList size={38} /></span>
              <i className="vq-orbit vq-orbit-one"><Sparkles size={18} /></i>
              <i className="vq-orbit vq-orbit-two"><FileQuestion size={18} /></i>
              <i className="vq-orbit vq-orbit-three"><ClipboardCheck size={18} /></i>
            </div>
          </section>

          <section className="vq-metrics" aria-label="Test summary">
            <article className="vq-metric vq-metric-blue"><span><ClipboardList size={21} /></span><div><strong>{isLoading ? "—" : quizzes.length}</strong><p>Total Tests</p></div></article>
            <article className="vq-metric vq-metric-teal"><span><ClipboardCheck size={21} /></span><div><strong>{isLoading ? "—" : assignedQuizCount}</strong><p>Assigned</p></div></article>
            <article className="vq-metric vq-metric-amber"><span><FileText size={21} /></span><div><strong>{isLoading ? "—" : quizzes.length - assignedQuizCount}</strong><p>Unassigned</p></div></article>
            <article className="vq-metric vq-metric-purple"><span><FileQuestion size={21} /></span><div><strong>{isLoading ? "—" : totalQuestionCount}</strong><p>Total Questions</p></div></article>
          </section>

          {!isLoading && recentQuizzes.length > 0 && (
            <section className="vq-recent" aria-labelledby="vq-recent-title">
              <div className="vq-recent-heading">
                <h2 id="vq-recent-title">Recent Tests</h2>
                <span>Recently created</span>
              </div>
              <div className="vq-card-grid">
                {recentQuizzes.map((quiz) => renderQuizCard(quiz, true))}
              </div>
            </section>
          )}

          <section className="vq-toolbar" aria-label="Test search and actions">
            <label className="vq-search-bar">
              <Search size={18} aria-hidden="true" />
              <input type="search" className="vq-search-input" placeholder="Search tests..." value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)} aria-label="Search tests by title, code, or subject" />
            </label>
            <div className="vq-toolbar-context">
              <span>{Object.keys(quizzesBySubject).length} subject{Object.keys(quizzesBySubject).length !== 1 ? "s" : ""}</span>
              <button type="button" className="vq-create-secondary" onClick={() => navigate("/create-quiz")}><Plus size={16} />Create Test</button>
            </div>
          </section>

          {isLoading ? (
            <div className="vq-loading"><span className="vq-loading-spinner" /><p>Loading tests…</p></div>
          ) : Object.keys(quizzesBySubject).length > 0 ? (
            <section className="vq-subject-list" aria-label="Tests grouped by subject">
              {Object.entries(quizzesBySubject).map(([subject, subjectQuizzes]) => (
                <div key={subject} className="vq-subject-group">
                  <button type="button" className="vq-subject-header" onClick={() => toggleSubject(subject)}
                    aria-expanded={Boolean(expandedSubjects[subject])}>
                    <span className="vq-subject-icon"><FileText size={19} /></span>
                    <span className="vq-subject-name">{subject}<small>{subjectQuizzes.length} test{subjectQuizzes.length !== 1 ? "s" : ""}</small></span>
                    <span className="vq-subject-count">{subjectQuizzes.length}</span>
                    <ChevronDown className={`vq-chevron${expandedSubjects[subject] ? " open" : ""}`} size={19} />
                  </button>
                  {expandedSubjects[subject] && (
                    <div className="vq-card-grid">
                      {subjectQuizzes.map((quiz) => renderQuizCard(quiz))}
                    </div>
                  )}
                </div>
              ))}
            </section>
          ) : (
            <div className="vq-empty">
              <span className="vq-empty-icon">{searchTerm ? <Search size={29} /> : <ClipboardList size={31} />}</span>
              <div><h2>{searchTerm ? "No tests match your search" : user?.role === "teacher" ? "No tests yet" : "No tests available"}</h2>
                <p>{searchTerm ? "Try a different title, code, or subject." : "Create your first assessment to get started."}</p></div>
              {!searchTerm && <button type="button" className="vq-create-primary" onClick={() => navigate("/create-quiz")}><Plus size={17} />Create Test</button>}
            </div>
          )}
        </main>
      </div>
      {assigningQuiz && (
        <AssignAssessmentModal
          quiz={assigningQuiz}
          onClose={handleCloseAssign}
          onAssignmentResult={handleAssignmentResult}
          returnFocusTo={assignTriggerRef.current}
        />
      )}
      {sharingQuiz && <OrganisationPublishDialog quiz={sharingQuiz} onClose={() => setSharingQuiz(null)} />}
    </div>
  );
};

export default ViewQuizzes;
